import store from '../../store/store';
import { StoreState } from '../../store/store.interface';
import { getCustomerStatus, getCustomerToken } from '../customer/customer';
import { getUserDataForUserAnalytics } from '../user-profile/user-profile.selectors';
import { postEventLoggerApiCall } from '../event-logger/event-logger.apis';
import { getError, getOrder } from './store/order-completion.selectors';
import { postAnalyticsEventApiCall } from '../analytics-proxy/analytics-proxy.api';

export default class OrderConfirmationLogs {
  /**
   * Logs the order data after emitting the order confirmation event
   *
   * @param orderId
   */
  static afterEmitOrderConfirmation(orderId: string) {
    const order = getOrder(store.getState() as StoreState);

    try {
      postEventLoggerApiCall(
        {
          uiDump: {
            eventName: 'after-emit-order-confirmation-analytics',
            orderId: orderId,
            rmsOrderId: order?.id,
            message: this.getMessage({
              step: 'after-emit',
              eventIsOnAdobeDataLayer: !!window.adobeDataLayer.find(
                (event) => event.event === 'mhecomm-order-confirmation'
              ),
              eventIsOnDataLayer: !!window.dataLayer.find(
                (event) => event.event === 'mhecomm-order-confirmation'
              )
            })
          }
        }
      )
    } catch (err) {
      console.log(`Log event fail: ${err}`);
    }
  }

  /**
   * Logs the order confirmation event
   *
   * @param orderId
   * @param details
   */
  static emitOrderConfirmation(orderId: string, details: any) {
    details.step = 'on-emit';
    try {
      postEventLoggerApiCall({
        uiDump: {
          eventName: 'mhecomm-order-confirmation',
          orderId: orderId,
          rmsOrderId: details.orderId,
          message: this.getMessage(details)
        }
      })
    } catch (err) {
      console.log(`Log event fail: ${err}`);
    }
  }

  /**
   * Logs the order data before emitting the order confirmation event
   *
   * @param orderId
   */
  static beforeEmitOrderConfirmation(orderId: string) {
    const order = getOrder(store.getState() as StoreState);

    try {
      postEventLoggerApiCall(
        {
          uiDump: {
            eventName: 'before-emit-order-confirmation-analytics',
            orderId: orderId,
            rmsOrderId: order?.id,
            message: this.getMessage({step: 'before-emit'})
          }
        }
      )
    } catch (err) {
      console.log(`Log event fail: ${err}`);
    }
  }

  /**
   * Logs the user data on page load
   *
   * @param orderId
   */
  static loggingUserInformation(orderId: string) {
    const userData = getUserDataForUserAnalytics(store.getState() as StoreState);
    const customerStatus = getCustomerStatus(store.getState() as StoreState);
    const customerToken = getCustomerToken(store.getState() as StoreState);

    try {
      postEventLoggerApiCall(
        {
          uiDump: {
            eventName: 'logging-user-information',
            orderId: orderId,
            message: this.getMessage({
              userData: JSON.stringify(userData),
              customerStatus,
              customerToken: customerToken?.substring(0, 50)
            })
          }
        }
      )
    } catch (err) {
      console.log(`Log event fail: ${err}`);
    }
  }

  /**
   * Logs the load order confirmation event including the order status and error
   *
   * @param orderId
   * @param orderStatus
   */
  static loadOrderConfirmation(orderId: string, orderStatus: string) {
    const error = getError(store.getState() as StoreState);

    try {
      postEventLoggerApiCall(
        {
          uiDump: {
            eventName: 'load-order-confirmation',
            orderId: orderId,
            message: this.getMessage({status: orderStatus, error})
          }
        }
      )
    } catch (err) {
      console.log(`Log event fail: ${err}`);
    }
  }

  static registerAnalyticsListeners(orderId: string) {
    this.registerTrackCallbacks(orderId);
  }

  private static registerTrackCallbacks(orderId: string) {
    if (window.s && typeof window.s === 'object') {
      window.s.registerPreTrackCallback(async (url: string) => {
        this.logAnalyticsCallbackEvent(orderId, 'pre-track-listener', {
          eventUrl: url,
        });
        try {
          await this.sendAnalyticsEvent(url);
        } catch (err) {
          console.log(`Event proxy fail: ${err}`);
          this.logAnalyticsCallbackEvent(orderId, 'track-listener-failed', {
            eventUrl: url,
            error: err
          });
        }
      });
      window.s.registerPostTrackCallback((url: string) => {
        this.logAnalyticsCallbackEvent(orderId, 'post-track-listener', {
          eventUrl: url,
        });
      });
    }
  }

  private static async sendAnalyticsEvent(url: string) {
    return postAnalyticsEventApiCall(
      {
        url: url,
        verbose: true
      }
    );
  }

  private static logAnalyticsCallbackEvent(orderId: string, event: string, body: any) {
    try {
      postEventLoggerApiCall(
        {
          uiDump: {
            orderId: orderId,
            eventName: event,
            message: this.getMessage(body)
          }
        }
      );
    } catch (err) {
      console.log(`Log event fail: ${err}`);
    }
  }

  private static getMessage(body: any) {
    body.isAnalyticsLoaded = !!(window.s && typeof window.s === 'object');
    body.isReadyToTrack = !!(window.s && window.s.isReadyToTrack());
    body.launchUrl = document.querySelector('script[src*="892eef3a6362"]')?.getAttribute("src");

    return JSON.stringify(body);
  }
}

